import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  ActivityIndicator,
  FlatList,
  useColorScheme,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useAppStore, useThemeColors } from '../src/academy_update/store';
import * as Sharing from 'expo-sharing';
import {
  useFeaturedCourses,
  useSkillCategories,
  useSkillGapAnalysis,
  useInternshipMatches,
  useStudentCertificates,
  useLearningPaths, // Assuming this hook exists based on usage
} from '../src/academy_update/useAcademyData';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolateColor,
  useDerivedValue,
} from 'react-native-reanimated';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Initialize QueryClient for React Query
const queryClient = new QueryClient();

// --- Helper Components (Ideally in separate files: /components/academy/...) ---

/**
 * Animated Card Wrapper for a more attractive design
 */
const AnimatedCard = ({ children, onPress, style, accessibilityLabel }) => {
  const scale = useSharedValue(1);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: scale.value }],
    };
  });

  const handlePressIn = () => {
    scale.value = withTiming(0.95, { duration: 150 });
  };

  const handlePressOut = () => {
    scale.value = withTiming(1, { duration: 150 });
  };

  return (
    <TouchableOpacity
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      activeOpacity={0.8}
      accessibilityLabel={accessibilityLabel}
    >
      <Animated.View style={[style, animatedStyle]}>{children}</Animated.View>
    </TouchableOpacity>
  );
};

/**
 * Progress Bar Component
 */
const ProgressBar = ({ progress, colors }) => {
  const width = useSharedValue(0);

  useEffect(() => {
    // Ensure progress is a number between 0 and 100
    const safeProgress = Math.min(100, Math.max(0, progress));
    width.value = withTiming(safeProgress, { duration: 1000 });
  }, [progress]);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      width: `${width.value}%`,
      backgroundColor: colors.primary,
    };
  });

  return (
    <View style={[styles.progressBarContainer, { backgroundColor: colors.border }]}>
      <Animated.View style={[styles.progressBar, animatedStyle]} />
    </View>
  );
};

/**
 * Renders a single featured course card
 */
const CourseCard = React.memo(({ course, navigation, colors }) => {
  const shareCourse = useCallback(async () => {
    try {
      const shareMessage = `Check out this amazing course: ${course.title} - ${course.description}. Find it on Jobeng Academy!`;
      await Sharing.shareAsync('https://jobeng.academy/course/' + course.id, {
        dialogTitle: 'Share Course',
        mimeType: 'text/plain',
        subject: course.title,
      });
    } catch (error) {
      console.error('Sharing failed:', error.message);
      // Consider showing a user-friendly error message
    }
  }, [course]);

  const navigateToDetail = useCallback(() => {
    navigation.navigate('CourseDetail', { courseId: course.id });
  }, [navigation, course.id]);

  return (
    <AnimatedCard
      onPress={navigateToDetail}
      accessibilityLabel={`Course: ${course.title}`}
      style={[
        styles.courseCard,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
    >
      <View
        style={[
          styles.courseIconContainer,
          { backgroundColor: course.color || colors.primary }, // Added fallback color
        ]}
      >
        <Ionicons name={course.icon || 'book-outline'} size={24} color="#FFFFFF" />
      </View>
      <Text style={[styles.courseTitle, { color: colors.text }]} numberOfLines={2}>
        {course.title}
      </Text>
      <Text style={[styles.courseDescription, { color: colors.textSecondary }]} numberOfLines={3}>
        {course.description}
      </Text>
      {/* Features and Rating */}
      <View style={styles.featuresContainer}>
        {course.duration && (
          <View style={[styles.featureBadge, { backgroundColor: colors.border }]}>
            <Ionicons name="time-outline" size={12} color={colors.textSecondary} />
            <Text style={[styles.featureText, { color: colors.textSecondary }]}>
              {course.duration}
            </Text>
          </View>
        )}
        {course.level && (
          <View style={[styles.featureBadge, { backgroundColor: colors.border }]}>
            <Ionicons name="speedometer-outline" size={12} color={colors.textSecondary} />
            <Text style={[styles.featureText, { color: colors.textSecondary }]}>
              {course.level}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.ratingContainer}>
        <Ionicons name="star" size={16} color={colors.warning} />
        <Text style={[styles.ratingText, { color: colors.text }]}>
          {course.rating || 'N/A'} ({course.reviews || 0})
        </Text>
      </View>
      <TouchableOpacity style={styles.shareButton} onPress={shareCourse}>
        <Ionicons name="share-social-outline" size={20} color={colors.primary} />
      </TouchableOpacity>
    </AnimatedCard>
  );
});

/**
 * Renders a single skill category card
 */
const CategoryCard = React.memo(({ category, navigation, colors }) => {
  const navigateToCategory = useCallback(() => {
    navigation.navigate('CourseList', { categoryId: category.id });
  }, [navigation, category.id]);

  return (
    <AnimatedCard
      onPress={navigateToCategory}
      accessibilityLabel={`Category: ${category.title}`}
      style={[
        styles.categoryCard,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
    >
      <View
        style={[
          styles.categoryIconContainer,
          { backgroundColor: category.color || colors.primary },
        ]}
      >
        <Ionicons name={category.icon || 'folder-outline'} size={32} color="#FFFFFF" />
      </View>
      <Text style={[styles.categoryTitle, { color: colors.text }]} numberOfLines={2}>
        {category.title}
      </Text>
    </AnimatedCard>
  );
});

/**
 * Renders the AI Skill Analysis Card
 */
const AISkillAnalysisCard = React.memo(({ resumeData, navigation, colors, t }) => {
  const targetJob = resumeData?.personalInfo?.profession || t('targetJobDefault');
  const currentSkills = resumeData?.skills;

  const {
    data: analysisData,
    isLoading,
    isError,
  } = useSkillGapAnalysis(currentSkills, targetJob);

  if (!resumeData) {
    return null;
  }

  return (
    <View style={[styles.analysisContainer, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <Text style={[styles.sectionTitle, { color: colors.text }]}>
        {t('aiAnalysisTitle')}
      </Text>
      <Text style={[styles.analysisDescription, { color: colors.textSecondary }]}>
        {t('aiAnalysisDescription', targetJob)}
      </Text>

      {isLoading && (
        <ActivityIndicator
          size={36}
          color={colors.primary}
          style={{ marginVertical: 20 }}
        />
      )}
      {isError && (
        <Text style={[styles.errorText, { color: colors.error }]}>
          {t('networkError')}
        </Text>
      )}
      {analysisData?.skillGap?.length > 0 && !isLoading && (
        <View style={styles.skillGapList}>
          {analysisData.skillGap.map((skill, index) => (
            <View
              key={index}
              style={[styles.skillItem, { backgroundColor: colors.border }]}
            >
              <Ionicons
                name="key-outline"
                size={16}
                color={colors.primary}
              />
              <Text style={[styles.skillText, { color: colors.primary }]}>
                {skill}
              </Text>
            </View>
          ))}
          <TouchableOpacity
            style={[styles.ctaButton, { backgroundColor: colors.accent }]}
            onPress={() => {
              // Navigate to a filtered course list based on AI recommendations
              navigation.navigate('CourseList', {
                recommendedIds: analysisData.recommendedCourses,
              });
            }}
          >
            <Text style={styles.ctaButtonText}>
              {t('viewRecommendedCourses')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
});

/**
 * Certificate & Internship Matching Card Component
 */
const CertificateInternshipCard = React.memo(({ certificate, matchedInternships, colors }) => {
  const fadeAnim = useSharedValue(0);

  useEffect(() => {
    fadeAnim.value = withTiming(1, { duration: 800 });
  }, []);

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: fadeAnim.value,
    };
  });

  return (
    <Animated.View
      style={[
        styles.certificateCard,
        { backgroundColor: colors.cardBackground, borderColor: colors.primary },
        animatedStyle,
      ]}
    >
      <View style={styles.certificateHeader}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.certificateTitle, { color: colors.text }]}>
            {certificate.name}
          </Text>
          <Text style={[styles.certificateDate, { color: colors.secondaryText }]}>
            Earned on {certificate.earnedDate}
          </Text>
        </View>
        <View
          style={[
            styles.certificateBadge,
            { backgroundColor: colors.primary },
          ]}
        >
          <MaterialCommunityIcons name="certificate" size={24} color="#fff" />
        </View>
      </View>

      {matchedInternships && matchedInternships.length > 0 && (
        <View style={styles.matchedOpportunitiesSection}>
          <Text style={[styles.matchedTitle, { color: colors.primary }]}>
            🎯 Matched Opportunities ({matchedInternships.length})
          </Text>
          {matchedInternships.map((internship, index) => (
            <View
              key={index}
              style={[
                styles.matchedInternshipItem,
                { borderLeftColor: colors.primary },
              ]}
            >
              <Text style={[styles.internshipName, { color: colors.text }]}>
                {internship.company} - {internship.position}
              </Text>
              <View style={styles.matchScoreContainer}>
                <Text style={[styles.matchScore, { color: colors.primary }]}>
                  Match: {internship.matchScore}%
                </Text>
              </View>
            </View>
          ))}
        </View>
      )}
    </Animated.View>
  );
});

/**
 * Internship Notification Card Component
 */
const InternshipNotificationCard = React.memo(({ notification, colors, onDismiss }) => {
  const slideAnim = useSharedValue(0);

  useEffect(() => {
    slideAnim.value = withTiming(1, { duration: 600 });
  }, []);

  const animatedStyle = useAnimatedStyle(() => {
    // Corrected animation logic: use slideAnim.value directly for interpolation
    const translateX = interpolateColor(
      slideAnim.value,
      [0, 1],
      [-300, 0]
    );
    return {
      transform: [
        {
          translateX: translateX,
        },
      ],
    };
  });

  return (
    <Animated.View
      style={[
        styles.notificationCard,
        { backgroundColor: colors.accentColor || colors.primary }, // Added fallback
        animatedStyle,
      ]}
    >
      <View style={styles.notificationContent}>
        <MaterialCommunityIcons
          name="bell-alert"
          size={24}
          color="#fff"
          style={styles.notificationIcon}
        />
        <View style={{ flex: 1 }}>
          <Text style={styles.notificationTitle}>
            New Opportunity: {notification.position}
          </Text>
          <Text style={styles.notificationCompany}>
            {notification.company} • {notification.matchScore}% match
          </Text>
        </View>
        <TouchableOpacity
          onPress={onDismiss}
          accessibilityLabel="Dismiss notification"
        >
          <Ionicons name="close" size={24} color="#fff" />
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
});

/**
 * Learning Path Card Component
 */
const LearningPathCard = React.memo(({ path, colors, navigation }) => {
  const navigateToPath = useCallback(() => {
    navigation.navigate('LearningPathDetail', { pathId: path.id });
  }, [navigation, path.id]);

  return (
    <AnimatedCard
      onPress={navigateToPath}
      accessibilityLabel={path.title}
      style={[styles.pathCard, { backgroundColor: colors.card, borderColor: colors.border }]}
    >
      <Ionicons name={path.icon || 'map-outline'} size={30} color={colors.primary} />
      <Text style={[styles.pathTitle, { color: colors.text }]} numberOfLines={1}>
        {path.title}
      </Text>
      <Text style={[styles.pathDescription, { color: colors.textSecondary }]} numberOfLines={2}>
        {path.description}
      </Text>
      <View style={styles.pathDetails}>
        <Ionicons name="time-outline" size={14} color={colors.textSecondary} />
        <Text style={[styles.pathDetailText, { color: colors.textSecondary }]}>
          {path.duration || 'N/A'}
        </Text>
      </View>
    </AnimatedCard>
  );
});

/**
 * Internship Card Component
 */
const InternshipCard = React.memo(({ internship, colors, navigation }) => {
  const navigateToInternship = useCallback(() => {
    navigation.navigate('InternshipDetail', { internshipId: internship.id });
  }, [navigation, internship.id]);

  return (
    <AnimatedCard
      onPress={navigateToInternship}
      accessibilityLabel={internship.title}
      style={[styles.internshipCard, { backgroundColor: colors.card, borderColor: colors.border }]}
    >
      <Ionicons name={internship.icon || 'briefcase-outline'} size={30} color={colors.primary} />
      <Text style={[styles.internshipTitle, { color: colors.text }]} numberOfLines={1}>
        {internship.title}
      </Text>
      <Text style={[styles.internshipCompany, { color: colors.textSecondary }]} numberOfLines={1}>
        {internship.company} - {internship.location}
      </Text>
      <View style={[styles.matchBadge, { backgroundColor: colors.primary + '10' }]}>
        <Text style={[styles.matchText, { color: colors.primary }]}>
          Match: {internship.matchScore || 'N/A'}
        </Text>
      </View>
    </AnimatedCard>
  );
});

// --- Main Screen Content Component ---

function JobengAcademyScreenContent({ navigation }) {
  // --- State and Hooks ---
  const [searchText, setSearchText] = useState('');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true); // State for header visibility

  // FIX: Use individual selectors to prevent re-rendering when unrelated state changes.
  // The previous object literal selector returned a new object on every render,
  // which is an unstable dependency for any hook that uses it.
  const isDarkMode = useAppStore((state) => state.isDarkMode);
  const toggleDarkMode = useAppStore((state) => state.toggleDarkMode);
  const currentLanguage = useAppStore((state) => state.currentLanguage);
  const setLanguage = useAppStore((state) => state.setLanguage);
  const t = useAppStore((state) => state.t);
  const userName = useAppStore((state) => state.userName);
  const resumeData = useAppStore((state) => state.resumeData);
  const isPremium = useAppStore((state) => state.isPremium);
  const subscriptionStatus = useAppStore((state) => state.subscriptionStatus);
  const completedCourses = useAppStore((state) => state.completedCourses);
  const totalCourses = useAppStore((state) => state.totalCourses);

  const colors = useThemeColors();

  // --- Data Fetching ---
  const { data: featuredCourses = [], isLoading: isCoursesLoading } = useFeaturedCourses();
  const { data: skillCategories = [], isLoading: isCategoriesLoading } = useSkillCategories();
  // Renamed to avoid redeclaration error from previous fix attempt
  const { data: allInternshipMatches = [], isLoading: isInternshipsLoading } = useInternshipMatches();
  const { data: studentCertificates = [], isLoading: isCertificatesLoading } = useStudentCertificates();
  const { data: learningPaths = [], isLoading: isPathsLoading } = useLearningPaths();

  // --- Derived State (useMemo for performance) ---
  const progressPercentage = useMemo(() => {
    if (totalCourses === 0) return 0;
    return Math.round((completedCourses / totalCourses) * 100);
  }, [completedCourses, totalCourses]);

  const filteredCourses = useMemo(() => {
    if (!searchText) return featuredCourses;
    const lowerSearchText = searchText.toLowerCase();
    return featuredCourses.filter(
      (course) =>
        course.title.toLowerCase().includes(lowerSearchText) ||
        course.description.toLowerCase().includes(lowerSearchText)
    );
  }, [featuredCourses, searchText]);

  const firstRecommendedCourse = filteredCourses[0];

  // --- Animations ---
  const headerOpacity = useSharedValue(0);
  const contentOpacity = useSharedValue(0);

  useEffect(() => {
    headerOpacity.value = withTiming(1, { duration: 500 });
    contentOpacity.value = withTiming(1, { duration: 800, delay: 100 });
  }, []);

  const headerBgStyle = useAnimatedStyle(() => {
    const backgroundColor = interpolateColor(
      headerOpacity.value,
      [0, 1],
      [colors.background, colors.card]
    );
    return {
      opacity: headerOpacity.value,
      backgroundColor,
    };
  });

  const animatedContentStyle = useAnimatedStyle(() => {
    return {
      opacity: contentOpacity.value,
    };
  });

  // --- Render Logic ---

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: colors.background }]}>
      <StatusBar
        barStyle={isDarkMode ? 'light-content' : 'dark-content'}
        backgroundColor={colors.background}
      />

      {/* Header */}
      <Animated.View style={[styles.header, headerBgStyle, { borderBottomColor: colors.border }]}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityLabel={t('goBack')}
        >
          <Ionicons name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>

        <View style={styles.headerTitleContainer}>
        <Text style={[styles.headerTitle, { color: colors.text }]}>
            {t('academyTitle') || 'Jobeng Academy'}
          </Text>
        </View>

        {/* Subscription Status Badge */}
        <View style={[styles.subscriptionBadge, { backgroundColor: isPremium ? colors.accent : colors.border }]}>
          <Text style={[styles.subscriptionText, { color: isPremium ? colors.card : colors.textSecondary }]}>
            {subscriptionStatus}
          </Text>
        </View>

        <TouchableOpacity style={styles.iconButton} onPress={toggleDarkMode} accessibilityLabel={t('toggleTheme')}>
          <Ionicons
            name={isDarkMode ? 'sunny-outline' : 'moon-outline'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.iconButton}
          onPress={() => setLanguage(currentLanguage === 'ar' ? 'en' : 'ar')}
          accessibilityLabel={t('changeLanguage')}
        >
          <Text style={{ color: colors.primary, fontWeight: 'bold' }}>
            {currentLanguage === 'ar' ? 'EN' : 'EN'}
          </Text>
        </TouchableOpacity>
      </Animated.View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
      >
        <Animated.View style={[styles.container, animatedContentStyle]}>
          {/* Welcome Title (Personalized) */}
          <Text style={[styles.welcomeTitle, { color: colors.text }]}>
            {t('welcomeTitle', { userName })}
          </Text>
          <Text style={[styles.welcomeDescription, { color: colors.textSecondary }]}>
            {t('welcomeDescription')}
          </Text>

          {/* Search Bar (Moved up for better UX) */}
          <View style={[styles.searchContainer, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <Ionicons
              name="search-outline"
              size={20}
              color={colors.textSecondary}
              style={styles.searchIcon}
            />
            <TextInput
              style={[styles.searchInput, { color: colors.text }]}
              placeholder={t('searchPlaceholder') || 'Search courses, paths, or skills...'}
              placeholderTextColor={colors.textSecondary}
              value={searchText}
              onChangeText={setSearchText}
              returnKeyType="search"
            />
            {searchText.length > 0 && (
              <TouchableOpacity onPress={() => setSearchText('')} style={styles.clearSearchButton}>
                <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            )}
          </View>

          {/* Start Learning CTA (First Recommended Course) */}
          {firstRecommendedCourse && (
            <TouchableOpacity
              style={[styles.startLearningButton, { backgroundColor: colors.primary }]}
              onPress={() => navigation.navigate('CourseDetailScreen', { courseId: firstRecommendedCourse.id })}
              accessibilityLabel={t('startLearning')}
            >
              <Text style={styles.startLearningButtonText}>
                {t('startLearning') || 'Start Learning'}: {firstRecommendedCourse.title}
              </Text>
              <Ionicons name="arrow-forward" size={20} color="#FFFFFF" style={{ marginLeft: 8 }} />
            </TouchableOpacity>
          )}

          {/* Progress Tracking */}
          <View style={[styles.progressSection, { backgroundColor: isDarkMode ? colors.card : colors.softBackground }]}>
            <Text style={[styles.progressText, { color: colors.text }]}>
              {t('yourProgress') || 'Your Progress'}: {completedCourses} of {totalCourses} {t('coursesCompleted') || 'Courses Completed'}
            </Text>
            <ProgressBar progress={progressPercentage} colors={colors} />
            {/* Certificate Button */}
            {progressPercentage === 100 && (
              <TouchableOpacity
                style={[styles.certificateButton, { backgroundColor: colors.accent }]}
                onPress={() => alert(t('certificateIssuedMock') || 'Certificate Issued! (Mock API Call)')}
                accessibilityLabel={t('issueCertificate')}
              >
                <Ionicons name="medal" size={20} color="#FFFFFF" />
                <Text style={styles.certificateButtonText}>{t('issueYourCertificate') || 'Issue Your Certificate'}</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Upgrade to Premium Card (Only for Free users) */}
          {!isPremium && (
            <AnimatedCard
              onPress={() => navigation.navigate('Subscription')}
              accessibilityLabel={t('upgradeToPremium')}
              style={[styles.upgradeCard, { backgroundColor: colors.primary, borderColor: colors.accent }]}
            >
              <View style={styles.upgradeContent}>
                <Ionicons name="star-outline" size={32} color={colors.accent} />
                <View style={styles.upgradeTextContainer}>
                  <Text style={styles.upgradeTitle}>{t('upgradeToPremium') || 'Upgrade to Premium'}</Text>
                  <Text style={styles.upgradeDescription}>{t('unlockAllFeatures') || 'Unlock all features and content.'}</Text>
                </View>
                <TouchableOpacity style={[styles.upgradeButton, { backgroundColor: colors.accent }]} onPress={() => navigation.navigate('SubscriptionScreen')}>
                  <Text style={styles.upgradeButtonText}>{t('upgradeButton') || 'Upgrade'}</Text>
                </TouchableOpacity>
              </View>
            </AnimatedCard>
          )}

          {/* Internship Notifications (Moved up for immediate visibility) */}
          {allInternshipMatches.length > 0 && (
            <View style={styles.notificationsSection}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                {t('newInternshipAlerts') || 'New Internship Alerts'}
              </Text>
              {allInternshipMatches.slice(0, 2).map((notification, index) => (
                <InternshipNotificationCard
                  key={index}
                  notification={notification}
                  colors={colors}
                  onDismiss={() => {
                    // In a real app, this would dispatch an action to dismiss the notification
                    alert(`Dismissed notification for ${notification.position}`);
                  }}
                />
              ))}
            </View>
          )}

          {/* AI Skill Analysis Card (High priority) */}
          <AISkillAnalysisCard resumeData={resumeData} navigation={navigation} colors={colors} t={t} />

          {/* Featured Courses Section */}
          <View style={styles.featuredCoursesSection}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              {t('featuredCourses') || 'Featured Courses'}
            </Text>
            {isCoursesLoading ? (
              <ActivityIndicator size={36} color={colors.primary} />
            ) : (
              <FlatList
                horizontal
                data={filteredCourses}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <CourseCard course={item} navigation={navigation} colors={colors} />
                )}
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.horizontalScrollView}
              />
            )}
          </View>

          {/* Learning Paths Section */}
          {learningPaths.length > 0 && (
            <View style={styles.learningPathSection}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                {t('learningPaths') || 'Learning Paths'}
              </Text>
              {isPathsLoading ? (
                <ActivityIndicator size={36} color={colors.primary} />
              ) : (
                <FlatList
                  horizontal
                  data={learningPaths}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => (
                    <LearningPathCard path={item} colors={colors} navigation={navigation} />
                  )}
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.horizontalScrollView}
                />
              )}
            </View>
          )}

          {/* Student Certificates & Matched Internships */}
          {studentCertificates.length > 0 && (
            <View style={styles.certificatesSection}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                {t('yourCertificates') || 'Your Certificates'}
              </Text>
              {isCertificatesLoading ? (
                <ActivityIndicator size={36} color={colors.primary} />
              ) : (
                studentCertificates.map((certificate, index) => {
                  // Find matched internships for this certificate (mock logic)
                  const matched = allInternshipMatches.filter(
                    (i) => i.certificateId === certificate.id
                  );
                  return (
                    <CertificateInternshipCard
                      key={index}
                      certificate={certificate}
                      matchedInternships={matched}
                      colors={colors}
                    />
                  );
                })
              )}
            </View>
          )}

          {/* All Skill Categories */}
          <View style={styles.skillCategoriesSection}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>
              {t('browseCategories') || 'Browse Categories'}
            </Text>
            {isCategoriesLoading ? (
              <ActivityIndicator size={36} color={colors.primary} />
            ) : (
              <View style={styles.categoryGrid}>
                {skillCategories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    category={category}
                    navigation={navigation}
                    colors={colors}
                  />
                ))}
              </View>
            )}
          </View>

          {/* All Internship Matches (If not already shown in notifications/certificates) */}
          {allInternshipMatches.length > 0 && (
            <View style={styles.internshipSection}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>
                {t('allInternshipMatches') || 'All Internship Matches'}
              </Text>
              {isInternshipsLoading ? (
                <ActivityIndicator size={36} color={colors.primary} />
              ) : (
                <FlatList
                  horizontal
                  data={allInternshipMatches}
                  keyExtractor={(item) => item.id}
                  renderItem={({ item }) => (
                    <InternshipCard internship={item} colors={colors} navigation={navigation} />
                  )}
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.horizontalScrollView}
                />
              )}
            </View>
          )}
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
}

// --- Styles (Consolidated and Cleaned) ---

const styles = StyleSheet.create({
  // Global Layout
  safeArea: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  container: {
    padding: 16,
    paddingBottom: 32,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  backButton: {
    padding: 8,
  },
  iconButton: {
    padding: 8,
    marginLeft: 4,
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  subscriptionBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 15,
    marginRight: 8,
  },
  subscriptionText: {
    fontSize: 12,
    fontWeight: '700',
  },

  // Welcome Section
  welcomeTitle: {
    fontSize: 28,
    fontWeight: '800',
    marginBottom: 8,
  },
  welcomeDescription: {
    fontSize: 16,
    marginBottom: 24,
  },

  // Search
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    paddingHorizontal: 16,
    borderWidth: 1,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 16,
  },
  clearSearchButton: {
    padding: 4,
  },

  // CTA Button
  startLearningButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 24,
    shadowColor: '#0e4e96',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 5,
    elevation: 5,
  },
  startLearningButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
  },

  // Progress Tracking
  progressSection: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 12,
  },
  progressText: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  progressBarContainer: {
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  certificateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    padding: 12,
    borderRadius: 10,
  },
  certificateButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 16,
    marginLeft: 8,
  },

  // Upgrade Card
  upgradeCard: {
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    borderWidth: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 4,
  },
  upgradeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  upgradeTextContainer: {
    flex: 1,
    marginLeft: 15,
  },
  upgradeTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#FFFFFF',
    marginBottom: 4,
  },
  upgradeDescription: {
    fontSize: 14,
    color: '#E0E0E0',
  },
  upgradeButton: {
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 8,
  },
  upgradeButtonText: {
    color: '#0F172A',
    fontWeight: '700',
    fontSize: 14,
  },

  // Section Headers
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 16,
  },
  horizontalScrollView: {
    paddingRight: 16,
    marginBottom: 24,
  },

  // Course Card
  featuredCoursesSection: {
    marginBottom: 24,
  },
  courseCard: {
    borderRadius: 16,
    padding: 16,
    width: 280,
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 4,
  },
  courseIconContainer: {
    width: 56,
    height: 56,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  courseTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  courseDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  featuresContainer: {
    flexDirection: 'row',
    marginTop: 8,
    marginBottom: 8,
  },
  featureBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
    marginRight: 8,
  },
  featureText: {
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  ratingText: {
    marginLeft: 4,
    fontSize: 14,
    fontWeight: '600',
  },
  shareButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    padding: 4,
  },

  // Category Grid
  skillCategoriesSection: {
    marginBottom: 24,
  },
  categoryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  categoryCard: {
    width: '48%',
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  categoryIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  categoryTitle: {
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },

  // AI Analysis Card
  analysisContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  analysisDescription: {
    fontSize: 15,
    marginBottom: 16,
    lineHeight: 22,
  },
  errorText: {
    fontSize: 14,
    textAlign: 'center',
    marginVertical: 16,
  },
  skillGapList: {
    marginTop: 8,
  },
  skillItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  skillText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  ctaButton: {
    padding: 14,
    borderRadius: 10,
    marginTop: 16,
    alignItems: 'center',
  },
  ctaButtonText: {
    color: '#FFFFFF',
    fontWeight: '700',
    fontSize: 16,
  },

  // Certificate & Internship Card
  certificatesSection: {
    marginBottom: 24,
  },
  certificateCard: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  certificateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  certificateTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  certificateDate: {
    fontSize: 12,
    fontWeight: '500',
  },
  certificateBadge: {
    width: 50,
    height: 50,
    borderRadius: 25,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  matchedOpportunitiesSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
  },
  matchedTitle: {
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  matchedInternshipItem: {
    paddingLeft: 12,
    paddingVertical: 8,
    borderLeftWidth: 3,
    marginBottom: 8,
  },
  internshipName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  matchScoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  matchScore: {
    fontSize: 12,
    fontWeight: '700',
  },

  // Internship Notification Card
  notificationsSection: {
    marginBottom: 24,
  },
  notificationCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  notificationContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  notificationIcon: {
    marginRight: 12,
  },
  notificationTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 2,
  },
  notificationCompany: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
  },

  // Learning Path Card
  learningPathSection: {
    marginBottom: 24,
  },
  pathCard: {
    width: 250,
    marginRight: 16,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  pathTitle: {
    fontSize: 18,
    fontWeight: '800',
    marginTop: 8,
  },
  pathDescription: {
    fontSize: 14,
    marginTop: 4,
    marginBottom: 8,
  },
  pathDetails: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pathDetailText: {
    fontSize: 12,
    marginLeft: 4,
  },

  // Internship Card
  internshipSection: {
    marginBottom: 24,
  },
  internshipCard: {
    width: 250,
    marginRight: 16,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  internshipTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginTop: 8,
  },
  internshipCompany: {
    fontSize: 14,
    marginTop: 4,
  },
  matchBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  matchText: {
    fontWeight: '700',
    fontSize: 12,
  },
});

// --- HOC Wrapper ---

export default function JobengAcademyScreen(props) {
  return (
    <QueryClientProvider client={queryClient}>
      <JobengAcademyScreenContent {...props} />
    </QueryClientProvider>
  );
}